/**
 * Preview audio for the main video track. The compositor only decodes *video*
 * frames (WebCodecs), so the source clip's audio would be silent. This plays it
 * back through a hidden <audio> element per source, kept in sync with the
 * playhead: the Player tells us the active clip + its source time each frame, and
 * we start/seek/stop the element to match. It's a preview approximation — the
 * ffmpeg export is the audio "truth" (per-clip atrim + adelay + amix).
 *
 * Sync model: the element runs on its own media clock while playing; we only
 * correct drift when it exceeds DRIFT_TOLERANCE. On pause/scrub we pause and
 * park the element at the exact source time so play() resumes aligned.
 */
export type UrlResolver = (assetId: string) => string | Promise<string>;

/** The active clip's audio state at the current playhead. */
export interface ActiveClipAudio {
  assetId: string;
  /** Time within the source media to be hearing right now (seconds). */
  sourceTimeSec: number;
  /** 0..1 clip volume. */
  volume: number;
}

const DRIFT_TOLERANCE_SEC = 0.3;

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

export class ClipAudioPlayer {
  private resolve: UrlResolver;
  private els = new Map<string, HTMLAudioElement>();
  /** Desired source time to seek to once an element finishes loading metadata. */
  private pending = new Map<string, number>();
  private activeId: string | null = null;

  constructor(resolve: UrlResolver) {
    this.resolve = resolve;
  }

  private elFor(assetId: string): HTMLAudioElement {
    let el = this.els.get(assetId);
    if (!el) {
      el = new Audio();
      el.preload = 'auto';
      // No crossOrigin: we only play (never read samples), so a plain cross-
      // origin (tainted) load is fine and avoids CORS-gating playback.
      const stored = el;
      el.addEventListener('loadedmetadata', () => {
        const want = this.pending.get(assetId);
        if (want !== undefined) {
          try {
            stored.currentTime = want;
          } catch {
            /* range not seekable yet */
          }
          this.pending.delete(assetId);
        }
      });
      this.els.set(assetId, el);
      Promise.resolve(this.resolve(assetId))
        .then((url) => {
          stored.src = url;
        })
        .catch(() => {
          /* asset unavailable — stays silent */
        });
    }
    return el;
  }

  private seekEl(el: HTMLAudioElement, assetId: string, timeSec: number): void {
    if (el.readyState >= 1) {
      try {
        el.currentTime = timeSec;
      } catch {
        /* not seekable yet */
      }
    } else {
      this.pending.set(assetId, timeSec); // applied on loadedmetadata
    }
  }

  /**
   * Reconcile playback with the playhead. `clip` is the active main-track clip
   * (or null between clips); `playing` is the transport state.
   */
  update(clip: ActiveClipAudio | null, playing: boolean): void {
    if (!clip) {
      this.pauseActive();
      return;
    }
    const el = this.elFor(clip.assetId);

    // Switched clips: pause the previous element, park the new one.
    if (this.activeId !== clip.assetId) {
      if (this.activeId) this.els.get(this.activeId)?.pause();
      this.activeId = clip.assetId;
      this.seekEl(el, clip.assetId, clip.sourceTimeSec);
    }

    el.volume = clamp01(clip.volume);

    if (playing) {
      if (
        el.readyState >= 1 &&
        Math.abs(el.currentTime - clip.sourceTimeSec) > DRIFT_TOLERANCE_SEC
      ) {
        this.seekEl(el, clip.assetId, clip.sourceTimeSec);
      }
      if (el.paused) void el.play().catch(() => {});
    } else {
      if (!el.paused) el.pause();
      this.seekEl(el, clip.assetId, clip.sourceTimeSec); // park for aligned resume
    }
  }

  /** Pause whatever is playing (used at end-of-play / when off any clip). */
  private pauseActive(): void {
    if (this.activeId) this.els.get(this.activeId)?.pause();
  }

  /**
   * Prewarm audio elements for the given sources so the FIRST clip isn't silent
   * while its <audio> lazily loads on play. Idempotent — elFor reuses existing
   * elements, so repeated calls (per edit) only create genuinely new ones.
   */
  preload(assetIds: string[]): void {
    for (const id of new Set(assetIds)) this.elFor(id);
  }

  pauseAll(): void {
    for (const el of this.els.values()) el.pause();
  }

  dispose(): void {
    for (const el of this.els.values()) {
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    this.els.clear();
    this.pending.clear();
    this.activeId = null;
  }
}
